import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {RoutineRunAs, TriggerType} from './RoutineTypes';
import {getRandomWeeklyScheduleDefaults} from './RoutineUtils';
import {FIELD_CHANGED} from './triggerConstants';
import {getDefaultTimeTriggerConfig, RoutineFormState} from './useRoutineForm';

export interface RoutineExample {
  id: string;
  label: string;
  icon: BenchlingIconName;
  formState: RoutineFormState;
}

// Hardcoded, tenant-agnostic examples that prefill the create panel. Trigger configs mirror the
// backend EVENT_TRIGGER_REGISTRY (object_type/field/to_values). No schema_id is set because it is
// tenant-specific; examples prefill to the "all objects of this type" scope and the user narrows it.
export const ROUTINE_EXAMPLES: Array<RoutineExample> = [
  {
    id: 'weekly-project-summary',
    label: 'Weekly project summary',
    icon: 'calendar-days',
    formState: {
      name: 'Weekly project summary',
      description: '',
      prompt:
        "Summarize the state of experiments across the projects I'm involved in over the past week, highlighting completed work, in-progress experiments, and anything that appears blocked. Include my work and my collaborators' work.",
      fileBlocks: [],
      triggerType: TriggerType.TIME_BASED,
      triggerConfig: {
        ...getDefaultTimeTriggerConfig(),
        freq_type: 'weekly',
        recurrence_interval: 1,
        // Randomize the weekday and hour so users starting from this example don't all schedule
        // at the same default time (previously Monday 8am), spreading out scheduled load.
        ...getRandomWeeklyScheduleDefaults(),
      },
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      runAs: RoutineRunAs.CREATOR,
      overrideRunAs: null,
      memoryEnabled: true,
    },
  },
  {
    id: 'review-study-design-before-execution',
    label: 'Review study design before execution',
    icon: 'copy',
    formState: {
      name: 'Review study design before execution',
      description: '',
      prompt:
        'A study was just moved into execution. Before work begins, check whether similar studies have already been run. Compare its objective, design, and key parameters against existing studies. Use that prior work to judge redundancy and assess design: whether the study is sized to give an interpretable result, whether controls and comparators are present, whether parameters sit in ranges that previously worked, whether known failure modes are addressed, or other insights you find. Cite the specific studies each of your points rests on. If you do not find any relevant past work, say so briefly. You are advising a human reviewer and do not hold or release studies.',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: {
        object_type: 'Study',
        trigger_kind: FIELD_CHANGED,
        field: 'phase',
        to_values: ['EXECUTION'],
        object_scopes: ['viewable'],
      },
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      runAs: RoutineRunAs.CREATOR,
      overrideRunAs: null,
      memoryEnabled: true,
    },
  },
  {
    id: 'compare-entries-historical',
    label: 'Compare new entries against historical data',
    icon: 'chart-line',
    formState: {
      name: 'Compare new entries against historical data',
      description: '',
      prompt: `You are a lab notebook review assistant. Your job is to put an entry in context against comparable prior work and surface anything a human reviewer should know, both problems and useful observations. You do not approve, reject, or make final determinations on entries.

STEP 1: Find comparable prior work
Identify prior entries that represent meaningfully similar work. Use your judgment about what makes two experiments comparable here, e.g. template used or assay performed. Prefer a smaller, genuinely similar set over a larger, loosely related one. Report which entries you selected and on what basis. If the comparison set is thin, heterogeneous, or spans a long time window, say so and note that conclusions are correspondingly weaker.

STEP 2: Analyze results against prior results
Treat the current results and the prior results as one body of evidence and analyze them together. Say what the comparison implies, e.g. whether this entry contradicts or extends what the prior work showed, whether this entry is inside or outside trends from prior results, whether the current result changes how the results should be read, or other insights you see. Cite the specific values and prior entries the analysis rests on. State how confident the comparison supports each conclusion.

STEP 3 — Compare methods and conditions:
Compare methods and conditions (e.g. protocol steps, materials, reagent lots, instruments, conditions) against the comparison set. Note deviations from the typical pattern. Cite which prior entry the comparison is relative to. Distinguish a deliberate-looking change from an apparent inconsistency.

OUTPUT FORMAT:
Begin with a short comparison summary: what this entry was compared against, how strong that comparison is, and the single most useful takeaway. Then return a structured list of notes with a summary table at the top, each with:
- Category (trend / discrepancy / general observation)
- Severity (informational / needs review / likely error)
- Specific citation (which data in this entry, which prior entries)`,
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: {
        object_type: 'Entry',
        trigger_kind: FIELD_CHANGED,
        field: 'review_status',
        to_values: ['NEEDS_REVIEW'],
        object_scopes: ['viewable'],
      },
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      runAs: RoutineRunAs.CREATOR,
      overrideRunAs: null,
      memoryEnabled: true,
    },
  },
];
